import { RefObject, useEffect, useState } from 'react';

export type ContainerSize = {
  width: number;
  height: number;
};

/**
 * Hook that tracks the dimensions of a container element using ResizeObserver.
 * Returns { width, height } that update whenever the element resizes.
 */
export function useContainerSize(ref: RefObject<HTMLElement>): ContainerSize {
  const [size, setSize] = useState<ContainerSize>({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const apply = (width: number, height: number) =>
      setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));

    const measure = () => apply(el.clientWidth, el.clientHeight);

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        apply(width, height);
      }
    });
    observer.observe(el);

    // Re-measure when the element scrolls back into view. The multi-table grid
    // panel is hidden by translating it off-screen (transform), not by
    // unmounting or display:none — and a transform doesn't change the box size,
    // so ResizeObserver never fires on hide/show. Without this, a cell that was
    // first measured before its layout had settled (or at zero size) keeps that
    // stale value on return to the grid; useResponsiveScale then reads width=0
    // as "full size", rendering oversized seats that overflow the cell and crop
    // the top info/controls. Re-measuring on viewport re-entry recalculates the
    // layout cleanly. Deduped, so it's a no-op when the size is already correct.
    let intersectionObserver: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== 'undefined') {
      intersectionObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) measure();
      });
      intersectionObserver.observe(el);
    }

    // A viewport resize can change the cell size without a per-element resize
    // event reaching us before the next paint; re-measure as a cheap backstop.
    window.addEventListener('resize', measure);

    // Set initial size
    measure();

    return () => {
      observer.disconnect();
      intersectionObserver?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [ref]);

  return size;
}
